<script setup lang="ts">
  import { ref, type HTMLAttributes } from 'vue'
  import type { ISODateTimeString } from '@gat-ship/types'
  import { IconCalendar } from '@gat-ship/icons'
  import {
    DateInput,
    useDatePicker,
    type DatePickerAnchorTime
  } from '../../date-input'
  import { DateCalendar } from '../../date-calendar'
  import { Popover, PopoverContent, PopoverTrigger } from '../popover'
  import InputGroup from './InputGroup.vue'
  import InputGroupAddon from './InputGroupAddon.vue'
  import InputGroupButton from './InputGroupButton.vue'

  /**
   * Composed, ready-to-use date field: free-type the date (parsed and
   * expanded on blur via `DateInput`) or pick it from a calendar popover
   * opened by the leading icon button. Owns its own `InputGroup` chrome,
   * so it is dropped in directly — `<InputGroupDate v-model … />` — rather
   * than slotted into an `InputGroup` by the caller.
   *
   * Both entry paths commit the same `ISODateTimeString` via
   * `update:modelValue`. A calendar pick is anchored to `anchorTime`
   * (noon by default — stable across timezones); typed edits keep any
   * time-of-day the model already carried.
   */
  const props = withDefaults(
    defineProps<{
      /** The bound instant. An empty string is treated as no value. */
      modelValue?: ISODateTimeString | string
      /** Forwarded to the input's `id` — wire to a `<label for>`. */
      name?: string
      placeholder?: string
      disabled?: boolean
      dataTestid?: string
      hasErrors?: boolean
      /** Roll a lone, already-passed day forward to next month. */
      futureDate?: boolean
      /** Time-of-day a calendar-picked date is anchored to. */
      anchorTime?: DatePickerAnchorTime
      /** Class applied to the `InputGroup` chrome (sizing, width, …). */
      class?: HTMLAttributes['class']
    }>(),
    { anchorTime: 'noon' }
  )

  const emit = defineEmits<{
    'update:modelValue': [value: ISODateTimeString | undefined]
    blur: []
  }>()

  // `id` is forwarded to the inner input via `name`; suppress attr
  // fallthrough so a passed `id` can't also land on the wrapper and
  // create a duplicate-id pair with the input.
  defineOptions({ inheritAttrs: false })

  const calendarOpen = ref(false)

  /** The bound instant for the picker bridge — empty string reads as unset. */
  function currentModel(): ISODateTimeString | undefined {
    return props.modelValue
      ? (props.modelValue as ISODateTimeString)
      : undefined
  }

  // Bridges the model to `DateCalendar`'s PlainDate model so the popover
  // and the typed input edit the same value.
  const { selectedDate, handleDateChange } = useDatePicker(
    {
      modelValue: currentModel,
      timeZone: undefined,
      anchorTime: () => props.anchorTime
    },
    (value) => emit('update:modelValue', value)
  )

  function onCalendarChange(date: Parameters<typeof handleDateChange>[0]) {
    handleDateChange(date)
    calendarOpen.value = false
  }

  function onDateInput(value: ISODateTimeString | undefined) {
    emit('update:modelValue', value)
  }
</script>

<template>
  <InputGroup :class="props.class">
    <InputGroupAddon>
      <Popover v-model:open="calendarOpen">
        <PopoverTrigger as-child>
          <InputGroupButton aria-label="Open calendar" :disabled="disabled">
            <IconCalendar class="size-4 shrink-0" />
          </InputGroupButton>
        </PopoverTrigger>
        <PopoverContent as-child align="start">
          <DateCalendar
            mode="single"
            :model-value="selectedDate"
            @update:model-value="onCalendarChange"
          />
        </PopoverContent>
      </Popover>
    </InputGroupAddon>
    <DateInput
      data-slot="input-group-control"
      :model-value="modelValue"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :data-testid="dataTestid"
      :has-errors="hasErrors"
      :future-date="futureDate"
      class="flex-1 rounded-none border-0 bg-transparent pl-1 tabular-nums shadow-none focus-visible:ring-0 dark:bg-transparent"
      @update:model-value="onDateInput"
      @blur="emit('blur')"
    />
  </InputGroup>
</template>
